import React from 'react';
import { TrendingUp, Shield, Users, Target, Award, BarChart3 } from 'lucide-react';
import { aboutContent, companyInfo } from '../mock/data';
import SEO from '../components/SEO';

const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Trust & Transparency',
      description: 'SEBI-registered with ethical, client-centric research practices.'
    },
    {
      icon: Target,
      title: 'Data-Driven Insights',
      description: 'Rigorous analysis combining technical and fundamental research.'
    },
    {
      icon: Award,
      title: 'Proven Expertise',
      description: `${companyInfo.experience} years of collective experience in equity markets.`
    },
    {
      icon: Users,
      title: 'Client Success',
      description: 'Dedicated to helping clients achieve their financial goals.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEO
        title="About Us | SEBI Registered Research Analyst | Equi Pulse"
        description="Learn about Equi Pulse Research - A SEBI registered research analyst firm with 12+ years of experience providing data-driven market intelligence and investment advisory in India."
        keywords="about equi pulse, SEBI registered analyst, research analyst India, investment advisor Indore, stock market expert"
        url="/about"
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1920')] bg-cover bg-center opacity-10" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Equi Pulse Research</span>
            </h1>
            <p className="text-xl text-gray-300">
              SEBI Registered Research Analyst with 12+ Years of Market Experience
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-emerald-700/30">
              <div className="inline-block p-3 bg-emerald-900/30 rounded-lg mb-6">
                <Target className="w-8 h-8 text-emerald-500" aria-hidden="true" />
              </div>
              <h2 className="text-3xl font-bold text-amber-400 mb-4">Our Mission</h2>
              <p className="text-gray-300 leading-relaxed">
                {aboutContent.mission}
              </p>
            </div>

            <div className="p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-amber-700/30">
              <div className="inline-block p-3 bg-amber-900/30 rounded-lg mb-6">
                <TrendingUp className="w-8 h-8 text-amber-500" aria-hidden="true" />
              </div>
              <h2 className="text-3xl font-bold text-emerald-400 mb-4">Our Vision</h2>
              <p className="text-gray-300 leading-relaxed">
                {aboutContent.vision}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Our <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Expertise</span>
              </h2>
            </div>

            <div className="space-y-8">
              <div className="p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-emerald-700/30">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-emerald-900/30 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-emerald-500" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-amber-400 mb-4">Professional Experience</h3>
                    <p className="text-gray-300 leading-relaxed">
                      {aboutContent.expertise}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-amber-700/30">
                <div className="flex items-start gap-4 mb-4">
                  <div className="p-3 bg-amber-900/30 rounded-lg">
                    <Shield className="w-6 h-6 text-amber-500" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-emerald-400 mb-4">Research Approach</h3>
                    <p className="text-gray-300 leading-relaxed">
                      {aboutContent.approach}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Our <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Core Values</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300"
                >
                  <div className="inline-block p-3 bg-emerald-900/30 rounded-lg mb-4">
                    <Icon className="w-8 h-8 text-emerald-500" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-bold text-amber-400 mb-3">{value.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-950 via-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-emerald-900/30 rounded-full mb-4">
                <Award className="w-12 h-12 text-emerald-500" aria-hidden="true" />
              </div>
              <h3 className="text-5xl font-bold text-white mb-2">{companyInfo.experience}</h3>
              <p className="text-gray-400">Years of Experience</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-amber-900/30 rounded-full mb-4">
                <Users className="w-12 h-12 text-amber-500" aria-hidden="true" />
              </div>
              <h3 className="text-5xl font-bold text-white mb-2">500+</h3>
              <p className="text-gray-400">Happy Clients</p>
            </div>

            <div className="text-center">
              <div className="inline-block p-4 bg-emerald-900/30 rounded-full mb-4">
                <Shield className="w-12 h-12 text-emerald-500" aria-hidden="true" />
              </div>
              <h3 className="text-5xl font-bold text-white mb-2">SEBI</h3>
              <p className="text-gray-400">Registered Analyst</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Start Your{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">
                Investment Journey?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Partner with us for data-driven insights and proven market strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 transition-all duration-300"
              >
                Get in Touch
              </a>
              <a
                href="/services"
                className="px-8 py-4 bg-slate-800/50 backdrop-blur-sm border border-amber-500/30 hover:bg-slate-800 text-white font-semibold rounded-lg transition-all duration-300"
              >
                View Services
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
